import { useApolloClient } from "@apollo/client";
import { AgentDialogFormValues } from "../../../features/agent/agentDialogFormValues.js";
import { CardItem, CollapseSection, Modal, ModalOpenButton } from "@msys/ui";
import { Edit as EditIcon } from "@mui/icons-material";
import {
  Checkbox,
  FormControlLabel,
  IconButton,
  Stack,
  Tooltip,
} from "@mui/material";
import { useTranslate } from "@tolgee/react";
import { Formik, FormikProps, useFormikContext } from "formik";
import { noop, uniqueId } from "lodash-es";
import { useSnackbar } from "notistack";
import React from "react";
import { QuotePdfCalculationType } from "../../../../clients/graphqlTypes.js";
import {
  closeAfterSend,
  CrmSendEmailForm,
  FormValues,
  isEmailAttachmentsTooLarge,
  useCrmSendEmail,
} from "../../crm/CrmSendEmailForm.js";
import { PdfDownloadArgs } from "../hooks/useDownloadQuotePdf.js";
import { SelectQuotePdf } from "../inputs/SelectQuotePdf.js";
import { QuotePreviewListItem } from "../QuotePreviewListItem.js";
import { usePublishQuoteMutation } from "../buttons/PublishQuoteButton.generated.js";
import {
  PublishQuoteAndSendViaEmailModal_ProjectFragment,
  PublishQuoteAndSendViaEmailModal_QuoteFragment,
} from "./PublishQuoteAndSendViaEmailModal.generated.js";
import { QuotePublishingInfoModal } from "./QuotePublishingInfoModal.js";
import { useSendQuoteViaEmail } from "./SendQuoteViaEmailModal.js";

/** Reports the form's attachment-size state to the surrounding modal, whose buttons live outside the Formik tree. */
function AttachmentsSizeReporter({
  onChange,
}: {
  onChange: (tooLarge: boolean) => void;
}) {
  const { values } = useFormikContext<FormValues>();
  const tooLarge = isEmailAttachmentsTooLarge(values.attachments);
  React.useEffect(() => {
    onChange(tooLarge);
    return () => onChange(false);
  }, [tooLarge, onChange]);
  return null;
}

interface Props {
  quoteId: string;
  quote: PublishQuoteAndSendViaEmailModal_QuoteFragment;
  projectId: string;
  project: PublishQuoteAndSendViaEmailModal_ProjectFragment;
  calculationType: QuotePdfCalculationType;
  pdfTemplateArgs?: PdfDownloadArgs | null;
  handleClose: () => void;
  handleComplete?: (handleClose: () => void) => Promise<void>;
}

export const PublishQuoteAndSendViaEmailModal = ({
  quote,
  projectId,
  quoteId,
  handleClose,
  handleComplete,
  calculationType,
  pdfTemplateArgs: passedPdfTemplateArgs,
}: Props) => {
  const { t } = useTranslate([
    "QuoteSend",
    "Global",
    "ProjectMembers",
    "CrmOrganisations",
  ]);
  const { enqueueSnackbar } = useSnackbar();

  const client = useApolloClient();
  const [publishQuote] = usePublishQuoteMutation({
    client,
  });

  const handlePublish = async () => {
    await publishQuote({
      variables: {
        input: {
          projectId,
          docId: quoteId,
        },
      },
    });
    enqueueSnackbar(t("Quote published", { ns: "QuoteSend" }));
  };

  const [isSubmitting, setIsSubmitting] = React.useState<boolean>(false);
  const [needSendQuote, setNeedSendQuote] = React.useState<boolean>(false);
  const [attachmentsTooLarge, setAttachmentsTooLarge] =
    React.useState<boolean>(false);

  const {
    recipientOptions,
    urlAttachments,
    getAttachmentsOnSubmit,
    query,
    pdfTemplateArgs,
    setPdfTemplateArgs,
  } = useSendQuoteViaEmail({
    projectId,
    quoteId,
    calculationType,
    pdfTemplateArgs: passedPdfTemplateArgs,
  });

  const {
    initialValues,
    validationSchema,
    handleSendEmail,
    emailTemplates,
    templatesLoading,
    templatesError,
  } = useCrmSendEmail({
    urlAttachments,
    getAttachmentsOnSubmit,
    emailTemplateContext: "QUOTE",
    context: { type: "QUOTE", id: quoteId },
  });

  const formikRef = React.useRef<FormikProps<FormValues>>(null);
  const formId = React.useMemo(() => uniqueId(), []);

  const isPublishedRef = React.useRef(false);

  // Leaving after a publish whose send failed still has to refresh the page.
  const handleCancel = async () => {
    if (isPublishedRef.current) {
      await closeAfterSend(handleClose, handleComplete);
    } else {
      handleClose();
    }
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    try {
      const values = formikRef.current?.values;

      // validate for sending quote
      if (needSendQuote) {
        if (!values) return;
        formikRef.current?.setTouched({
          recipient: true,
          subject: true,
          body: true,
        });
        await formikRef.current?.validateForm();
        if (!formikRef.current?.isValid) return;
        if (!values.recipient || !values.recipient.crmCompanyId) return;
      }

      if (!isPublishedRef.current) {
        await handlePublish();
        isPublishedRef.current = true;
      }

      if (needSendQuote) {
        if (values) {
          // On a failure the quote is published by now; the modal stays open
          // with its values so the user can fix the problem and retry the send.
          if (!(await handleSendEmail(values))) return;
          enqueueSnackbar(
            t("Quote was sent to client", {
              ns: "QuoteSend",
            })
          );
        }
      }

      await closeAfterSend(handleClose, handleComplete);
    } catch (e) {
      throw e;
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Modal
      id="publish-quote-modal"
      handleClose={handleCancel}
      title={t("Publish quote", { ns: "QuoteSend" })}
      isLoading={query.loading || templatesLoading}
      actionButtons={[
        {
          label: t("Cancel", { ns: "Global" }),
          handleClick: handleCancel,
          buttonProps: {
            variant: "text",
            disabled: isSubmitting,
          },
        },
        {
          label: t("Publish", { ns: "Global" }),
          handleClick: handleSubmit,
          buttonProps: {
            loading: isSubmitting,
            disabled: needSendQuote && attachmentsTooLarge,
          },
        },
      ]}
      maxWidth="md"
    >
      <Stack direction="column" spacing={2}>
        <CardItem>
          <QuotePreviewListItem
            quote={quote}
            Action={
              <ModalOpenButton
                Modal={QuotePublishingInfoModal}
                modalProps={{ projectId, quoteId }}
              >
                <IconButton size="small" color="primary">
                  <Tooltip title={t("Edit", { ns: "Global" })}>
                    <EditIcon fontSize="small" />
                  </Tooltip>
                </IconButton>
              </ModalOpenButton>
            }
          />
        </CardItem>

        <FormControlLabel
          control={
            <Checkbox
              size="medium"
              checked={needSendQuote}
              onChange={(event, checked) => {
                setNeedSendQuote(checked);
              }}
            />
          }
          label={t("Send to customer by email", { ns: "QuoteSend" })}
        />

        {needSendQuote && (
          <Formik
            innerRef={formikRef}
            initialValues={initialValues}
            enableReinitialize
            onSubmit={noop}
            validationSchema={validationSchema}
            validateOnChange={false}
            validateOnBlur={false}
          >
            <>
              <AgentDialogFormValues />
              <AttachmentsSizeReporter onChange={setAttachmentsTooLarge} />
              <CrmSendEmailForm
                recipientOptions={recipientOptions}
                additionalFields={
                  <CollapseSection
                    title={t("PDF attachment", { ns: "QuoteSend" })}
                    isInitiallyExpanded={true}
                  >
                    <SelectQuotePdf
                      doc={quote}
                      label={t("Choose PDF template", { ns: "QuoteSend" })}
                      value={pdfTemplateArgs}
                      onChange={setPdfTemplateArgs}
                      calculationType={calculationType}
                    />
                  </CollapseSection>
                }
                formId={formId}
                availableTemplates={emailTemplates}
                context={{ type: "QUOTE", id: quote.id }}
                emailTemplateContext="QUOTE"
                templatesLoading={templatesLoading}
                templatesError={templatesError}
              />
            </>
          </Formik>
        )}
      </Stack>
    </Modal>
  );
};
